import {
  deraRequiredComment,
  DeraOneToFiveScaleOptions,
  DeraQuestionType,
  DeraSubsectionLookup,
} from './dera-questions.interface';

// See dera-background.ts for the question/subsection format. Questions are stubbed pending final content.
export const DeraEducationSubsections: { [key: string]: DeraSubsectionLookup } = {
  formalEducation: {
    displayValue: 'Formal Education',
    questions: {
      educationHistory: {
        displayValue: '',
        type: DeraQuestionType.Custom,
      },
    },
  },
  educationProgramDetails: {
    displayValue: 'Education, Training or Program Details',
    optional: true,
    questions: {},
  },
  motivationForLearning: {
    displayValue: 'Motivation for Learning',
    questions: {
      interestedInLearning: {
        displayValue: 'Interest in learning new skills or training',
        type: DeraQuestionType.Radio,
        required: true,
        requiredMessage: 'Please select an option',
        options: {
          interested: { displayValue: 'Interested' },
          unsurePossiblyInterested: { displayValue: 'Unsure / possibly interested' },
          notInterested: { displayValue: 'Not interested' },
        },
        comment: {
          placeholder:
            'Add any relevant details regarding the person’s motivation for learning.\nSpecify any type(s) of training of interest mentioned.',
        },
      },
      whatHelpsThePersonLeanr: {
        displayValue: 'What helps the person learn',
        type: DeraQuestionType.CheckboxList,
        required: true,
        requiredMessage: 'Please select an option',
        options: {
          quietPlace: { displayValue: 'A quiet place to learn' },
          practiceOverTime: { displayValue: 'Doing it more than once / practice over time' },
          picturesDiagrams: { displayValue: 'Pictures, diagrams, or visual examples' },
          readingInstructions: { displayValue: 'Reading instructions or reading instructions aloud or written steps' },
          talkingItThrough: { displayValue: 'Talking it through or having someone explain it' },
          handsOnPractice: { displayValue: 'Trying it out / hands-on practice' },
          watchingDemonstration: { displayValue: 'Watching someone demonstrate how to do it' },
          writtenInstructions: { displayValue: 'Written instructions' },
          usingTechnology: { displayValue: 'Using technology' },
          learningSupports: { displayValue: 'Learning supports, strategies or accommodations used at school' },
          other: { displayValue: 'Other, please specify (requires text in the comment box)' },
        },

        comment: deraRequiredComment(
          'other',
          'Add any relevant details regarding learning supports and strategies that were successful in the past and/or might be useful in the future Include any recommendations from past learning assessments, individualized program plans (IPPs) from school, etc.'
        ),
      },
    },
  },
  essentialSkills: {
    displayValue: 'Essential Skills',
    questions: {
      reading: {
        displayValue: 'Reading',
        type: DeraQuestionType.Scale,
        required: true,
        requiredMessage: 'Please select an option',
        options: DeraOneToFiveScaleOptions,
        comment: deraRequiredComment('unknown', 'Add any relevant details regarding the person’s reading skills'),
        tooltip: {
          title: 'Reading',
          items: [
            'Does not read English',
            'Can manage reading in day-to-day life, e.g., shopping, but struggles to read basic English-language documents.',
            'Can read basic English-language documents; reading proficiency limits employment options.',
            'English-language reading skills sufficient for multiple employment contexts.',
            'Strong English-language reading proficiency, able to understand and use complex written information.',
            { label: '?', text: 'Insufficient information to assess (requires text in comment box)' },
          ],
        },
      },
      writing: {
        displayValue: 'Writing',
        type: DeraQuestionType.Scale,
        required: true,
        requiredMessage: 'Please select an option',
        options: DeraOneToFiveScaleOptions,
        comment: deraRequiredComment('unknown', 'Add any relevant details regarding the person’s writing skills'),
        tooltip: {
          title: 'Writing',
          items: [
            'Does not write English',
            'Can manage basic writing in day-to-day life, e.g., short notes, forms, but struggles to write simple sentences or complete basic documents in English',
            'Can write basic communications in English; writing proficiency limits employment options',
            'English-language writing skills sufficient for multiple employment contexts.',
            'Strong English-language writing skills; able to clearly communicate and use written language to convey complex information.',
            { label: '?', text: 'Insufficient information to assess (requires text in comment box)' },
          ],
        },
      },
      spokenEnglish: {
        displayValue: 'Spoken English',
        type: DeraQuestionType.Scale,
        required: true,
        requiredMessage: 'Please select an option',
        options: DeraOneToFiveScaleOptions,
        comment: deraRequiredComment(
          'unknown',
          'Add any relevant details regarding the person’s Spoken English skills'
        ),
        tooltip: {
          title: 'Spoken English',
          items: [
            'Does not use spoken English to communicate / Primary forms of communication are non-spoken, e.g., sign language, non-verbal communication.',
            'Spoken English permits communication of basic needs but communication barriers in day-to-day situations, e.g., while shopping, are common.',
            'Uses spoken English to communicate ideas; spoken fluency permits participation in some types of interactions.',
            'Spoken English permits most types of interactions with minimal communication barriers.',
            'Strong spoken English communication skills; able to express ideas clearly, engage in discussions, and convey complex information effectively.',
            { label: '?', text: 'Insufficient information to assess (requires text in comment box)' },
          ],
        },
      },
      preferredCommunicationMethods: {
        displayValue: 'Preferred communication methods - select all that apply',
        type: DeraQuestionType.CheckboxList,
        required: true,
        requiredMessage: 'Preferred communication methods is required',
        options: {
          phoneOrVideo: { displayValue: 'By phone or video call' },
          inPerson: { displayValue: 'In-person' },
          inWriting: { displayValue: 'In writing (text or email)' },
          other: { displayValue: 'Other, please specify (requires text in comment box)' },
        },
        comment: deraRequiredComment(
          'other',
          'Add any relevant details regarding the person’s preferred communication method(s)\nNote fluency in other languages, including American Sign Language'
        ),
      },
      digitalSkills: {
        displayValue: 'Digital skills',
        type: DeraQuestionType.Scale,
        required: true,
        requiredMessage: 'Please select an option',
        options: DeraOneToFiveScaleOptions,
        comment: deraRequiredComment('unknown', 'Add any relevant details regarding the person’s digital skills'),
        tooltip: {
          title: 'Digital skills',
          items: [
            'Does not use digital devices or technology.',
            'Can complete basic digital tasks in day-to-day life, e.g., simple phone use, sending texts, but struggles with common programs / apps or online navigation.',
            'Can use multiple types of devices with support to complete basic online tasks, e.g., email, forms, web searching.',
            'Can complete most digital tasks required in day-to-day life with little to no support, e.g., email, documents, online systems.',
            'Strong digital skills; able to use a range of devices, tools and platforms, navigate systems efficiently, and complete complex tasks online',
            { label: '?', text: 'Insufficient information to assess (requires text in comment box)' },
          ],
        },
      },
      internetAndDeviceAccess: {
        displayValue: 'Internet and device access?',
        type: DeraQuestionType.Radio,
        required: true,
        requiredMessage: 'Please select an option',
        options: {
          yesAtHome: { displayValue: 'Yes, at home' },
          yesFriendOrRelativeHome: { displayValue: 'Yes, at a friend or relative’s home' },
          yesPublicLocation: { displayValue: 'Yes, at a public location, e.g., library, community centre' },
          no: { displayValue: 'No' },
        },
      },
      problemSolving: {
        displayValue: 'Problem-solving',
        type: DeraQuestionType.Scale,
        required: true,
        requiredMessage: 'Please select an option',
        options: DeraOneToFiveScaleOptions,
        comment: deraRequiredComment(
          'unknown',
          'Add any relevant details regarding the person’s problem-solving skills'
        ),
        tooltip: {
          title: 'Problem-solving',
          items: [
            'Unable to solve problems or make decisions independently.',
            'Can manage simple problem-solving in day-to-day situations, e.g., routine choices, but struggles with unfamiliar or multi-step problems.',
            'Can solve straightforward problems, might need support sometimes.',
            'Can manage problem-solving in most contexts with little to no support.',
            'Strong problem-solving skills; able to analyze situations, consider options, and make effective decisions to address complex problems.',
            { label: '?', text: 'Insufficient information to assess (requires text in comment box)' },
          ],
        },
      },
      managementOfPersonalFinances: {
        displayValue: 'Management of personal finances',
        type: DeraQuestionType.Scale,
        required: true,
        requiredMessage: 'Please select an option',
        options: DeraOneToFiveScaleOptions,
        comment: deraRequiredComment(
          'unknown',
          'Add any relevant details regarding the person’s essential skills, i.e., abilities to function effectively in everyday life, learning, and work.\nNote any accommodations needed to maintain or further develop their skills'
        ),
        tooltip: {
          title: 'Management of personal finances',
          items: [
            'Unable to manage personal finances or make basic financial decisions / Has a financial administrator or trustee in place.',
            'Can independently complete simple financial tasks, e.g., making purchases, but requires help with budgeting, paying bills, or understanding financial obligations.',
            'Can independently complete more complex financial tasks, e.g., paying bills, using a bank account, basic budgeting, but requires help with planning, tracking, or complex financial decisions.',
            'Can manage day-to-day finances and most financial responsibilities, e.g., budgeting, bill payments, planning, with little to no support.',
            'Strong financial management skills; able to budget effectively, plan for future needs, make informed financial decisions, and manage complex financial responsibilities independently.',
            { label: '?', text: 'Insufficient information to assess (requires text in comment box)' },
          ],
        },
      },
    },
  },
};
